import { ZIP_BUNDLE_EVENTS } from 'Sp/Angular/Events/Client/Gallery';

export default [
    '$rootScope',
    '$scope',
    '$interval',
    '$routeParams',
    '$window',
    'spAppData',
    'spClientCredentials',
    'SPClientData',
    function BundleController(
        $rootScope,
        $scope,
        $interval,
        $routeParams,
        $window,
        spAppData,
        spClientCredentials,
        SPClientData
    ) {
        const PAGE_STATE = {
            AUTHORIZE: 'authorize',
            BROWSER_GOOGLE_SEARCH_APP: 'browser-google-search-app',
            PENDING: 'pending',
            READY: 'ready',
            TIMEOUT: 'timeout'
        };
        // Suffix appended to a copy key to name what the bundle holds - e.g. `zipBundleTitle`
        // plus `Slideshows`, or `zipBundle.pending.header` plus `Video`.
        const MEDIA_TYPE_KEY_SUFFIX = {
            DEFAULT: '',
            MEDIA_ITEMS: 'MediaItems',
            SLIDESHOW: 'Slideshow',
            SLIDESHOWS: 'Slideshows',
            VIDEO: 'Video',
            VIDEOS: 'Videos'
        };
        // If the ZIP Bundle has no email set, there's no reason for us to
        // present the form for authentication.  Instead, just make a
        // separate request to fetch the ZIP Bundle data.
        const clientCredentialsEmail = spClientCredentials.get().readableEmail;
        const clientCredentialsZipBundleKey = spClientCredentials.get().zipBundleKey;
        let pollingTimer = null;

        $scope.PAGE_STATE = PAGE_STATE;
        $scope.pageState = PAGE_STATE.AUTHORIZE;
        $scope.zipBundleAppData = spAppData.get('zipBundleData');
        $scope.zipBundleData = null;
        $scope.showHelp = false;
        $scope.credentials = { email: clientCredentialsEmail };
        $scope.errors = {};

        const isGoogleSearchApp = !!$window.navigator.userAgent.match(/(?:GSA)\/(\d+(\.?_?\d+)+)/i);

        if (isGoogleSearchApp) {
            $scope.pageState = PAGE_STATE.BROWSER_GOOGLE_SEARCH_APP;
            return;
        }

        if (!$scope.zipBundleAppData.settings.hasEmail || clientCredentialsEmail) {
            authenticateBundle();
        }

        $scope.authenticateBundle = authenticateBundle;

        // Picks the copy variant that reflects what's actually in the bundle: the
        // "photos" default only when photos are the only thing present, a
        // singular/plural variant for a single media type (video or slideshow)
        // on its own, or a generic "media items" variant once more than one type
        // is mixed together.
        function getMediaTypeKeySuffix(photoCount, videoCount, slideshowCount) {
            const presentTypeCount = [photoCount, videoCount, slideshowCount].filter(
                (count) => count > 0
            ).length;

            if (presentTypeCount > 1) {
                return MEDIA_TYPE_KEY_SUFFIX.MEDIA_ITEMS;
            }

            if (slideshowCount > 0) {
                return slideshowCount === 1
                    ? MEDIA_TYPE_KEY_SUFFIX.SLIDESHOW
                    : MEDIA_TYPE_KEY_SUFFIX.SLIDESHOWS;
            }

            if (videoCount > 0) {
                return videoCount === 1
                    ? MEDIA_TYPE_KEY_SUFFIX.VIDEO
                    : MEDIA_TYPE_KEY_SUFFIX.VIDEOS;
            }

            // Either photos only, or nothing known yet - both fall back to the
            // photo wording.
            return MEDIA_TYPE_KEY_SUFFIX.DEFAULT;
        }

        // Titles the ready state from the download links the bundle actually returned.
        $scope.getZipBundleTitleKey = function getZipBundleTitleKey() {
            const zipBundleData = $scope.zipBundleData || {};

            return (
                'zipBundleTitle' +
                getMediaTypeKeySuffix(
                    (zipBundleData.files || []).length,
                    (zipBundleData.videos || []).length,
                    (zipBundleData.slideshows || []).length
                )
            );
        };

        // Names the loading copy (both the pending and "taking longer than expected"
        // states) after the bundle's contents. The download links don't exist yet while
        // the bundle is still being packaged, so this uses the item counts the page was
        // rendered with instead.
        $scope.getZipBundleLoadingKey = function getZipBundleLoadingKey(name) {
            const contents = ($scope.zipBundleAppData || {}).contents || {};

            return (
                'zipBundle.' +
                name +
                getMediaTypeKeySuffix(
                    contents.photoCount || 0,
                    contents.videoCount || 0,
                    contents.slideshowCount || 0
                )
            );
        };

        $scope.downloadFile = function downloadFile(zipBundleFile) {
            SPClientData.logBundleDownloaded(
                $routeParams.zipBundleId,
                function doNothing() {
                    // Success
                },
                function doNothing() {
                    // Error
                }
            );

            $window.location.href = zipBundleFile.url;
        };

        function authenticateBundle() {
            SPClientData.bundleAuth(
                $routeParams.zipBundleId,
                $scope.credentials.email,
                $scope.credentials.doesAcceptTerms,
                function withSuccessResponse({ zipBundleData }) {
                    $scope.zipBundleData = zipBundleData;

                    if (zipBundleData.ready) {
                        $scope.pageState = PAGE_STATE.READY;

                        return;
                    }

                    pollForZipBundle();

                    $scope.pageState = PAGE_STATE.PENDING;
                },
                function withErrorResponse(response) {
                    if ($scope.pageState === PAGE_STATE.AUTHORIZE) {
                        $scope.errors = response.errors;
                    } else {
                        if (response.errors.email && response.errors.email.notSame) {
                            $scope.errors = null;
                            $scope.credentials.email = null;
                            $scope.pageState = PAGE_STATE.AUTHORIZE;
                        }
                    }
                }
            );
        }

        function pollForZipBundle() {
            // Already polling (e.g. authenticateBundle() re-reported "not ready" while a
            // poll was already in flight) - don't start a second overlapping timer/listener.
            if (pollingTimer) {
                return;
            }

            const POLLING_INTERVAL = 5000;
            // Show the "taking longer than expected" message after ~60s of polling, but
            // keep polling quietly in the background well past that: a bundle containing
            // a slideshow has to wait on an actual video render (an external, asynchronous
            // encode) before it can finish, which routinely takes longer than a plain
            // photo/video zip. If it finishes while this tab stays open, the page should
            // flip to ready on its own instead of requiring a manual refresh.
            const ATTEMPTS_BEFORE_TIMEOUT_MESSAGE = 60000 / POLLING_INTERVAL;
            // Absolute safety net so a bundle that's genuinely stuck (never reaches
            // Downloadable or Unrecoverable Error) doesn't poll forever.
            const MAX_ATTEMPTS = (15 * 60 * 1000) / POLLING_INTERVAL;
            let attempts = 0;

            const offZipBundleGot = $rootScope.$on(ZIP_BUNDLE_EVENTS.GOT, (_, response) => {
                // A slideshow renders outside the zip pipeline, so the bundle reaches Downloadable
                // while its render is still running - and a render that expires on its own is
                // replaced without the status ever leaving Downloadable. Waiting on the status
                // alone would leave this page showing "processing" until a manual refresh.
                if (response.zipBundleStatusId === 3 && !response.hasSlideshowRenderInFlight) {
                    stopPollingForZipBundle();
                    authenticateBundle();
                } else if (response.zipBundleStatusId === 7) {
                    // Unrecoverable Error - this bundle will never become ready, so give up
                    // rather than continuing to check forever in the background.
                    giveUpPolling();
                }
            });

            function stopPollingForZipBundle() {
                $interval.cancel(pollingTimer);
                pollingTimer = null;
                offZipBundleGot();
            }

            // Stop polling and show the same "taking longer than expected" messaging used
            // once the timeout threshold passes - there is no separate error page state, so
            // any terminal non-ready outcome (missing key, unrecoverable error, exceeding the
            // absolute safety net) surfaces the same way a plain timeout would.
            function giveUpPolling() {
                stopPollingForZipBundle();
                $scope.pageState = PAGE_STATE.TIMEOUT;
            }

            pollingTimer = $interval(() => {
                const zipBundleKey =
                    clientCredentialsZipBundleKey || $scope.zipBundleData.bundleKey;

                if (!zipBundleKey) {
                    giveUpPolling();
                    return;
                }

                attempts += 1;

                if (attempts === ATTEMPTS_BEFORE_TIMEOUT_MESSAGE) {
                    $scope.pageState = PAGE_STATE.TIMEOUT;
                }

                if (attempts >= MAX_ATTEMPTS) {
                    stopPollingForZipBundle();
                    return;
                }

                $rootScope.$emit(ZIP_BUNDLE_EVENTS.GET, {
                    zipBundleId: $routeParams.zipBundleId,
                    zipBundleKey
                });
            }, POLLING_INTERVAL);

            $scope.$on('$destroy', stopPollingForZipBundle);
        }
    }
];
